import React, { useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { CloudDownload as DownloadIcon, CloudUpload as UploadIcon } from '@mui/icons-material'
import { useGetUsersQuery, useRestoreAdminUserBackupMutation } from '@store/api/adminApi'
import { useAppSelector } from '@store/hooks'
import AdminPageGate from '@components/auth/AdminPageGate'

const MAX_BACKUP_BYTES = 5 * 1024 * 1024
const PAGE_SIZE = 15

const BackupManagement: React.FC = () => {
  const token = useAppSelector((state) => state.auth.token)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [restoreUserId, setRestoreUserId] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const { data: usersData, isLoading } = useGetUsersQuery({
    page,
    limit: PAGE_SIZE,
    search: search.trim() || undefined,
    sortBy: 'email',
    sortOrder: 'asc',
  })
  const [restoreBackup, { isLoading: isRestoring }] = useRestoreAdminUserBackupMutation()

  const users = usersData?.data ?? []

  const handleDownload = async (userId: string, email: string) => {
    setMessage(null)
    setDownloadingId(userId)
    try {
      const response = await fetch(`/api/v1/admin/users/${userId}/backup`, {
        headers: { Authorization: `Bearer ${token ?? ''}` },
      })
      if (!response.ok) {
        const body = await response.json().catch(() => null)
        throw new Error(body?.message || 'Download failed')
      }
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      const dateStr = new Date().toISOString().split('T')[0]
      const safeEmail = email.replace(/[^a-zA-Z0-9._-]/g, '_')
      link.download = `cigartrack-backup-${safeEmail}-${dateStr}.json`
      link.click()
      URL.revokeObjectURL(objectUrl)
      setMessage({ type: 'success', text: `Backup downloaded for ${email}` })
    } catch (err: unknown) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to download backup',
      })
    } finally {
      setDownloadingId(null)
    }
  }

  const handleRestoreClick = (userId: string) => {
    setRestoreUserId(userId)
    fileInputRef.current?.click()
  }

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !restoreUserId) return

    setMessage(null)
    if (file.size > MAX_BACKUP_BYTES) {
      setMessage({ type: 'error', text: 'Backup file is too large. Maximum is 5 MB.' })
      setRestoreUserId(null)
      return
    }

    try {
      const backup = JSON.parse(await file.text())
      const result = await restoreBackup({ userId: restoreUserId, backup }).unwrap()
      const stats = result.data?.result
      setMessage({
        type: 'success',
        text: `Backup restored. Humidors added: ${stats?.restoredHumidors ?? 0}, imported: ${stats?.imported ?? 0}, merged: ${stats?.merged ?? 0}.`,
      })
    } catch (err: unknown) {
      const apiErr = err as { data?: { message?: string } }
      setMessage({
        type: 'error',
        text: apiErr?.data?.message ?? 'Restore failed. Check the backup file and try again.',
      })
    } finally {
      setRestoreUserId(null)
    }
  }

  return (
    <AdminPageGate permission="users:read" title="User Backups">
      <Box>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          User Backups
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Download or restore collection JSON backups on behalf of users (support operations). Restore
          requires admin role and merges data into the selected account.
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={handleFileSelected}
        />

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              label="Search users by email or username"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              size="small"
              fullWidth
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            {isLoading ? (
              <CircularProgress size={28} />
            ) : users.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No users match this search.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Typography variant="body2">{user.email}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{user.username}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              disabled={downloadingId === user.id}
                              onClick={() => handleDownload(user.id, user.email)}
                            >
                              {downloadingId === user.id ? 'Downloading…' : 'Download'}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="warning"
                              startIcon={<UploadIcon />}
                              disabled={isRestoring && restoreUserId === user.id}
                              onClick={() => handleRestoreClick(user.id)}
                            >
                              Restore
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminPageGate>
  )
}

export default BackupManagement
